import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Stack,
  Typography,
} from "@mui/material";
import { getSkillAgentBrandCollections } from "../../api/Skills/skillAgentSearchesApi";

export function sortBrandsAlpha(brands = []) {
  return [...(Array.isArray(brands) ? brands : [])].sort((a, b) =>
    String(a.name || a.id).localeCompare(String(b.name || b.id), "es", {
      sensitivity: "base",
    }),
  );
}

function brandSources(brand) {
  if (Array.isArray(brand?.sources) && brand.sources.length) {
    return brand.sources;
  }
  return (brand?.urls || []).map((url) => ({
    url,
    label: menuLabelFromUrl(url),
    defaultSelected:
      /jacket|outerwear|coat|veste|chaqueta|blouson|cuir|abrigo/i.test(url),
  }));
}

/** Label estilo menú (no el slug crudo). */
export function menuLabelFromUrl(url) {
  const raw = String(url || "").toLowerCase();
  let path = raw;
  try {
    path = new URL(url).pathname.toLowerCase();
  } catch {
    /* keep */
  }
  if (/vestes-blousons/.test(path)) return "VESTES & BLOUSONS";
  if (/walk-x-schott|vestes-en-cuir/.test(path)) return "VESTES EN CUIR";
  if (/pantalones|pantalons/.test(path)) return "PANTALONES";
  if (/trousers/.test(path)) return "TROUSERS";
  if (/(^|\/)pants?(?:\/|$|-)|cargo-pants|men-pants|mens-pants/.test(path)) {
    return "PANTS";
  }
  if (/hoodies?/.test(path)) return "HOODIES";
  if (/sweatshirts?|\/sweats(?:\/|$)/.test(path)) return "SWEATSHIRTS";
  if (/pullovers?/.test(path)) return "PULLOVERS";
  if (/windbreakers?/.test(path)) return "WINDBREAKERS";
  if (/tracksuits?|track-jackets?/.test(path)) return "TRACK";
  if (/fleece/.test(path)) return "FLEECE";
  if (/suede/.test(path)) return "SUEDE";
  if (/chaquetas/.test(path)) return "CHAQUETAS";
  if (
    /jackets-and-outerwear|mens-jackets-and-coats|mens-jackets-coats|kith-outerwear|mens-outerwear|men-jackets|coats-jackets|outerwear-jackets/.test(
      path,
    )
  ) {
    return "OUTERWEAR & JACKETS";
  }
  if (
    /\/jackets?(\/|$)|archive-jackets|denim-jackets|jackets-all|bomber-jackets/.test(
      path,
    )
  ) {
    return "JACKETS";
  }
  if (/outerwear/.test(path)) return "OUTERWEAR";
  if (
    /new-arrivals|new-in|novedades|nouveautes|mens-new|novedad|new-arrival/.test(
      path,
    ) ||
    /\/new(\/|$)/.test(path)
  ) {
    return "NEW ARRIVALS";
  }
  if (/best-sellers|bestseller/.test(path)) return "BEST SELLERS";
  if (/shop-all|\/all(\/|$)|all-items|available-items/.test(path))
    return "SHOP ALL";
  if (/tracksuits?|survet/.test(path)) return "TRACKSUITS";
  if (/coat|abrigo/.test(path)) return "COATS";
  return collectionLabel(url).toUpperCase();
}

/** Pants family when the tipología names bottoms and not jackets. */
export function isPantsTipologia(tipologia) {
  const raw = String(tipologia || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
  const hasPants =
    /pants|pantalon|trouser|jogger|sweatpant|jeans|chino|bottoms/.test(raw);
  const hasOuter =
    /jacket|outerwear|\bcoats?\b|bomber|puffer|parka|chaqueta|veste|blouson|abrigo|cazadora/.test(
      raw,
    );
  return hasPants && !hasOuter;
}

const MENU_MODIFIERS = new Set([
  "zip",
  "full",
  "and",
  "the",
  "with",
  "con",
  "de",
  "avec",
]);
const MENU_GENERIC = new Set([
  "jacket",
  "jackets",
  "coat",
  "coats",
  "outerwear",
  "chaqueta",
  "chaquetas",
  "veste",
  "blouson",
  "abrigo",
]);
const MENU_FILLER = new Set([
  "urban",
  "moderno",
  "moderna",
  "novedad",
  "novedades",
  "new",
  "hombre",
  "mens",
  "men",
]);
const KEYWORD_SLUGS = {
  sweatshirt: ["sweatshirts", "hoodies", "sweats", "sudaderas"],
  hoodie: ["hoodies", "sweatshirts", "sweats"],
  sudadera: ["sweatshirts", "hoodies", "sweats", "sudaderas"],
  sudaderas: ["sweatshirts", "hoodies", "sweats", "sudaderas"],
  capucha: ["hoodies", "sweatshirts"],
  pullover: ["pullovers"],
  pull: ["pullovers"],
  fleece: ["fleece", "fleeces"],
  polar: ["fleece"],
  polaire: ["fleece"],
  windbreaker: ["windbreakers"],
  cortavientos: ["windbreakers"],
  track: ["track-jackets", "tracksuits"],
  tracksuit: ["tracksuits"],
  chandal: ["tracksuits"],
  suede: ["suede"],
  suedette: ["suedette", "suede"],
  zip: ["full-zip"],
};

const FINISH_WORDS = new Set([
  "bonded",
  "suede",
  "suedette",
  "leather",
  "nylon",
  "ripstop",
  "wool",
  "cotton",
  "denim",
  "canvas",
  "quilted",
  "sherpa",
  "shearling",
]);

function chunkSignalsOuterwear(chunk) {
  return /jacket|outerwear|\bcoats?\b|bomber|puffer|parka|overshirt|fleece|quilted|workwear|trucker|blazer|track|windbreaker|anorak|shell|chaqueta|veste|blouson|bombardier|surchemise|doudoune|manteau|abrigo|cazadora/.test(
    String(chunk || ""),
  );
}

function chunkOpensKeyword(chunk) {
  const p = String(chunk || "");
  if (chunkSignalsOuterwear(p)) return false;
  if (/pants|pantalon|trouser|jogger|sweatpant|jeans|chino|bottoms/.test(p)) {
    return false;
  }
  const words = p
    .trim()
    .split(/\s+/)
    .filter(
      (w) =>
        w.length >= 3 &&
        !MENU_MODIFIERS.has(w) &&
        !MENU_FILLER.has(w) &&
        !MENU_GENERIC.has(w),
    );
  if (!words.length) return false;
  if (words.every((w) => FINISH_WORDS.has(w))) return false;
  return true;
}

const langCache = new Map();

function normWord(value) {
  return String(value || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim();
}

function withPlurals(word) {
  const w = normWord(word);
  if (!w) return [];
  const extra = [];
  if (w === "scarf") extra.push("scarves");
  return extra;
}

/** Guarda la traducción EN/ES/FR de una palabra para menús y títulos. */
export function rememberSearchAlts(word, alts = []) {
  const list = [
    ...new Set(
      [
        normWord(word),
        ...alts.map(normWord),
        ...alts.flatMap(withPlurals),
      ].filter((w) => w && w.length >= 3),
    ),
  ];
  if (!list.length) return [];
  for (const w of list) langCache.set(w, list);
  return list;
}

function readGtx(data) {
  return normWord(data?.[0]?.[0]?.[0]);
}

/** Traduce cada palabra a inglés, español y francés. Devuelve true si hay algo nuevo. */
export async function translateMenuWords(words = []) {
  let learned = false;
  for (const raw of words) {
    const w = normWord(raw);
    if (!w || w.length < 3) continue;
    if (langCache.has(w) && langCache.get(w).length > 1) continue;
    const group = GARMENT_LANG.find((g) =>
      g.some((alt) => normWord(alt) === w),
    );
    if (group) {
      rememberSearchAlts(w, group);
      continue;
    }
    const found = [w];
    await Promise.all(
      ["en", "es", "fr"].map(async (tl) => {
        try {
          const res = await fetch(
            `https://translate.googleapis.com/translate_a/single?client=gtx&sl=auto&tl=${tl}&dt=t&q=${encodeURIComponent(w)}`,
          );
          if (!res.ok) return;
          const hit = readGtx(await res.json());
          if (hit) found.push(hit);
        } catch {
          /* se queda la palabra escrita */
        }
      }),
    );
    const before = langCache.get(w)?.length || 1;
    const list = rememberSearchAlts(w, found);
    if (list.length > before) learned = true;
  }
  return learned;
}

const GARMENT_LANG = [
  [
    "sweatshirt",
    "sweatshirts",
    "sweat",
    "sweats",
    "sweat-shirt",
    "sudadera",
    "sudaderas",
    "hoodie",
    "hoodies",
    "capucha",
  ],
  ["pullover", "pullovers", "pull"],
  ["fleece", "fleeces", "polar", "polaire"],
  ["windbreaker", "windbreakers", "cortavientos", "coupe-vent"],
  [
    "track",
    "tracksuit",
    "tracksuits",
    "track-jacket",
    "track-jackets",
    "chandal",
    "survetement",
  ],
];

function garmentAlts(word) {
  const w = normWord(word);
  if (langCache.has(w)) return langCache.get(w);
  const group = GARMENT_LANG.find((g) => g.some((alt) => normWord(alt) === w));
  return group ? group.map(normWord) : [w];
}

function textHasAlt(text, alt) {
  const body = String(alt || "")
    .toLowerCase()
    .replace(/[.*+?^${}()|[\]\\]/g, "\\$&")
    .replace(/\\-/g, "[-\\s]?");
  if (!body) return false;
  return new RegExp(`(?:^|[^a-z])${body}s?(?:[^a-z]|$)`, "i").test(
    normWord(text),
  );
}

/** Collection words from the tipología. "fleece jackets" → fleece, not every jacket. */
export function menuTokensForTipologia(tipologia) {
  if (isPantsTipologia(tipologia)) return [];
  const raw = String(tipologia || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
  const chunks = /[,|;]/.test(raw) ? raw.split(/[,|;]+/) : [raw];
  if (!chunks.some((chunk) => chunkOpensKeyword(chunk))) return [];
  const out = [];
  for (const chunk of chunks) {
    const words = chunk
      .trim()
      .split(/\s+/)
      .filter((w) => w && !MENU_MODIFIERS.has(w));
    const use = words.filter(
      (w) => !MENU_GENERIC.has(w) && !MENU_FILLER.has(w) && w.length >= 3,
    );
    if (!use.length || use.every((w) => MENU_GENERIC.has(w))) continue;
    out.push(use);
  }
  return out;
}

export function urlMatchesMenuTokens(url, tokens = []) {
  const s = String(url || "").toLowerCase();
  return tokens.some((words) =>
    words.every((w) => garmentAlts(w).some((alt) => textHasAlt(s, alt))),
  );
}

export function selectionMatchesMenuTokens(urlSelection = {}, tipologia = "") {
  const tokens = menuTokensForTipologia(tipologia);
  if (!tokens.length) return false;
  return Object.values(urlSelection || {}).some(
    (urls) =>
      Array.isArray(urls) && urls.some((u) => urlMatchesMenuTokens(u, tokens)),
  );
}

function keywordUrlsFromBrand(brandSourcesList, tokens) {
  let origin = "";
  let style = "collections";
  for (const s of brandSourcesList) {
    try {
      const u = new URL(String(s.url || ""));
      origin = u.origin;
      if (/\/shop\//i.test(u.pathname)) style = "shop";
      if (origin) break;
    } catch {
      /* next */
    }
  }
  if (!origin) return [];
  const seen = new Set();
  const out = [];
  for (const words of tokens) {
    const fromLang = [
      ...new Set(
        garmentAlts(words[0]).flatMap((alt) => {
          const slug = String(alt || "").replace(/\s+/g, "-");
          if (!slug) return [];
          return /s$/.test(slug) ? [slug] : [slug, `${slug}s`];
        }),
      ),
    ];
    const mapped = KEYWORD_SLUGS[words[0]] || fromLang;
    for (const slug of mapped) {
      if (seen.has(slug)) continue;
      seen.add(slug);
      const url = `${origin}/${style}/${slug}`;
      seen.add(slug);
      out.push({
        url,
        label: menuLabelFromUrl(url),
        defaultSelected: true,
      });
    }
  }
  return out.slice(0, 8);
}

export function brandHasWebMenu(brand) {
  if (brand?.shopifyMenu === false) return false;
  if (brand?.shopifyMenu === true) return true;
  return (
    (brand?.urls || []).some((u) => /\/collections\//i.test(u)) ||
    (brand?.sources || []).some((s) => /\/collections\//i.test(s.url || s))
  );
}

/** Marca en la selección las colecciones vivas que encajan con la tipología. */
export function applyLiveMenuSelection(
  urlSelection = {},
  brand,
  collections = [],
  tipologia = "",
) {
  const cols = Array.isArray(collections) ? collections : [];
  if (!brand?.id || !cols.length) return urlSelection;
  const sources = sourcesForTipologia(brand, tipologia, cols);
  const preferred = sources.filter((s) => s.defaultSelected);
  if (!preferred.length) return urlSelection;
  return {
    ...urlSelection,
    [brand.id]: preferred.map((s) => s.url),
  };
}

export function tipologiaMenuKey(tipologia) {
  if (isPantsTipologia(tipologia)) return "pants";
  const tokens = menuTokensForTipologia(tipologia);
  if (tokens.length) return `kw:${tokens.map((w) => w.join("+")).join("|")}`;
  return "outerwear";
}

export function urlIsPantsCollection(url) {
  const s = String(url || "").toLowerCase();
  if (
    /jacket|outerwear|chaqueta|blouson|veste|abrigo/.test(s) &&
    !/pants|trouser|pantalon/.test(s)
  ) {
    return false;
  }
  return (
    /\/(pants?|trousers?|pantalones?|pantalons?|cargo-pants|jeans|bottoms|chinos)(?:\/|$|\?|-)/.test(
      s,
    ) || /(?:men|mens)-pants(?:\/|$|\?)/.test(s)
  );
}

function urlIsOuterwearCollection(url) {
  const s = String(url || "").toLowerCase();
  if (urlIsPantsCollection(s)) return false;
  return /jacket|outerwear|coat|veste|chaqueta|blouson|abrigo|bomber|puffer/.test(
    s,
  );
}

/** Same path rewrite as the backend safety net, so the checkbox shows pants. */
function rewriteJacketUrlToPants(url) {
  let origin = "";
  let path = "";
  try {
    const u = new URL(String(url || ""));
    origin = u.origin;
    path = u.pathname;
  } catch {
    return [];
  }
  const rules = [
    [/mens-jackets-and-coats/i, "mens-pants"],
    [/mens-jackets-coats/i, "mens-pants"],
    [/jackets-and-outerwear/i, "pants"],
    [/men-jackets/i, "men-pants"],
    [/men-outerwear/i, "men-pants"],
    [/mens-outerwear/i, "mens-pants"],
    [/denim-jackets/i, "pants"],
    [/bomber-jackets/i, "pants"],
    [/archive-jackets/i, "pants"],
    [/jackets-fleeces/i, "pants"],
    [/elevated-outerwear/i, "pants"],
    [/vestes-blousons/i, "pantalons"],
    [/chaquetas/i, "pantalones"],
    [/outerwear/i, "pants"],
    [/jackets/i, "pants"],
  ];
  const out = [];
  for (const [re, to] of rules) {
    if (!re.test(path)) continue;
    const next = path.replace(re, to);
    if (next !== path) out.push(origin + next);
    break;
  }
  if (/\/collections\//i.test(path)) {
    out.push(`${origin}/collections/pants`);
    out.push(`${origin}/collections/trousers`);
  } else if (/\/shop\//i.test(path)) {
    out.push(`${origin}/shop/pants`);
  }
  return [...new Set(out)];
}

export function selectionHasPantsUrl(urlSelection = {}) {
  return Object.values(urlSelection || {}).some(
    (urls) => Array.isArray(urls) && urls.some((u) => urlIsPantsCollection(u)),
  );
}

/**
 * Sources shown for a brand. Pants tipología checks pants/trousers
 * (real menu, or a sibling URL when the catalog only has jackets).
 */
export function sourcesForTipologia(brand, tipologia = "", liveSources) {
  const base =
    Array.isArray(liveSources) && liveSources.length
      ? liveSources
      : brandSources(brand);
  if (!isPantsTipologia(tipologia)) {
    const tokens = menuTokensForTipologia(tipologia);
    if (!tokens.length) return base;
    const mapped = base.map((s) => ({
      ...s,
      defaultSelected: urlMatchesMenuTokens(
        `${s.url} ${s.label || ""}`,
        tokens,
      ),
    }));
    if (mapped.some((s) => s.defaultSelected)) {
      return [
        ...mapped.filter((s) => s.defaultSelected),
        ...mapped.filter((s) => !s.defaultSelected),
      ];
    }
    // Lujo / no-Shopify: no inventar /collections/leather. Mantén el PLP
    // del catálogo marcado (el filtro tipología ya afina leather/suede).
    if (!brandHasWebMenu(brand)) {
      return base.map((s) => ({ ...s, defaultSelected: true }));
    }
    const extra = keywordUrlsFromBrand(base, tokens).filter(
      (s) => !base.some((b) => b.url === s.url),
    );
    return [...extra, ...mapped];
  }
  const mapped = base.map((s) => ({
    ...s,
    defaultSelected: urlIsPantsCollection(s.url),
  }));
  if (mapped.some((s) => s.defaultSelected)) {
    return [
      ...mapped.filter((s) => s.defaultSelected),
      ...mapped.filter((s) => !s.defaultSelected),
    ];
  }
  const extra = [];
  const seen = new Set(base.map((s) => s.url));
  for (const s of base) {
    if (!urlIsOuterwearCollection(s.url)) continue;
    for (const u of rewriteJacketUrlToPants(s.url)) {
      if (seen.has(u)) continue;
      seen.add(u);
      extra.push({
        url: u,
        label: menuLabelFromUrl(u),
        defaultSelected: true,
      });
    }
  }
  return [...extra, ...mapped];
}

function httpUrls(sources) {
  return (sources || [])
    .map((s) => s.url)
    .filter((u) => /^https?:\/\//i.test(String(u || "").trim()));
}

/** Default: family of the tipología. Jackets stay on outerwear. */
export function buildDefaultUrlSelection(
  brands = [],
  brandIds = [],
  tipologia = "",
) {
  const idSet = new Set(brandIds || []);
  const pants = isPantsTipologia(tipologia);
  const narrow = pants || menuTokensForTipologia(tipologia).length > 0;
  const out = {};
  for (const b of brands || []) {
    if (!idSet.has(b.id)) continue;
    const sources = sourcesForTipologia(b, tipologia);
    const preferred = sources.filter((s) => s.defaultSelected);
    const picked = preferred.length ? preferred : narrow ? [] : sources;
    out[b.id] = httpUrls(picked);
  }
  return out;
}

/**
 * Drop non-http leftovers (e.g. label "Zalando") and rebuild from catalog if empty.
 */
/** Fake Shopify guesses for leather/suede on non-Shopify brands. */
function isGuessedKeywordCollectionUrl(url) {
  return /\/collections\/(leather|leathers|suede|suedette|shearling|shearlings|ante|antes|cuero|cuirs?)(?:\/|$|\?)/i.test(
    String(url || ""),
  );
}

export function healUrlSelection(
  urlSelection = {},
  brands = [],
  brandIds = [],
  tipologia = "",
) {
  const idSet = new Set(brandIds || []);
  const next = {
    ...(urlSelection && typeof urlSelection === "object" ? urlSelection : {}),
  };
  for (const b of brands || []) {
    if (!idSet.has(b.id)) continue;
    const sel = Array.isArray(next[b.id]) ? next[b.id] : null;
    if (!sel) continue;
    let http = sel
      .map((u) => String(u || "").trim())
      .filter((u) => /^https?:\/\//i.test(u));
    // Quitar /collections/leather inventadas en marcas sin menú Shopify.
    if (!brandHasWebMenu(b)) {
      http = http.filter((u) => !isGuessedKeywordCollectionUrl(u));
    }
    if (!http.length) {
      const rebuilt = buildDefaultUrlSelection([b], [b.id], tipologia)[b.id];
      if (rebuilt?.length) next[b.id] = rebuilt;
      else delete next[b.id];
    } else if (http.length !== sel.length) {
      next[b.id] = http;
    }
  }
  if (isPantsTipologia(tipologia)) {
    for (const b of brands || []) {
      if (!idSet.has(b.id)) continue;
      const urls = Array.isArray(next[b.id]) ? next[b.id] : [];
      if (urls.some((u) => urlIsPantsCollection(u))) continue;
      const rebuilt = buildDefaultUrlSelection([b], [b.id], tipologia)[b.id];
      if (rebuilt?.length) next[b.id] = rebuilt;
    }
  }
  const tokens = menuTokensForTipologia(tipologia);
  if (tokens.length) {
    for (const b of brands || []) {
      if (!idSet.has(b.id)) continue;
      const urls = Array.isArray(next[b.id]) ? next[b.id] : [];
      if (urls.some((u) => urlMatchesMenuTokens(u, tokens))) continue;
      const rebuilt = buildDefaultUrlSelection([b], [b.id], tipologia)[b.id];
      if (rebuilt?.length) next[b.id] = rebuilt;
    }
  }
  return next;
}

export function collectionLabel(url) {
  try {
    const u = new URL(url);
    const parts = u.pathname.split("/").filter(Boolean);
    const last = parts[parts.length - 1] || u.hostname;
    return decodeURIComponent(last).replace(/-/g, " ");
  } catch {
    return String(url || "")
      .replace(/^https?:\/\//, "")
      .slice(0, 48);
  }
}

/**
 * Checklist de marcas + filtros/colecciones (labels del menú web).
 */
export default function SkillAgentBrandSourcesFields({
  brands = [],
  brandIds = [],
  urlSelection = {},
  tipologia = "",
  onBrandIdsChange,
  onUrlSelectionChange,
  maxHeight = 320,
}) {
  const { t } = useTranslation();
  const sorted = sortBrandsAlpha(brands);
  const [menuByBrand, setMenuByBrand] = useState({});
  const [loadingMenu, setLoadingMenu] = useState({});
  const [menuError, setMenuError] = useState({});
  const familyRef = useRef(null);
  const menuRef = useRef(menuByBrand);
  const selRef = useRef(urlSelection);
  const genRef = useRef(0);
  const [menusLeft, setMenusLeft] = useState(0);
  menuRef.current = menuByBrand;
  selRef.current = urlSelection;

  const sourcesFor = (brand) =>
    sourcesForTipologia(brand, tipologia, menuByBrand[brand.id]);

  useEffect(() => {
    const nextKey = tipologiaMenuKey(tipologia);
    const prev = familyRef.current;
    familyRef.current = nextKey;
    if (prev === nextKey) return;
    if (prev === null && nextKey === "outerwear") return;
    if (
      prev === null &&
      nextKey === "pants" &&
      selectionHasPantsUrl(urlSelection)
    ) {
      return;
    }
    if (
      prev === null &&
      nextKey.startsWith("kw:") &&
      selectionMatchesMenuTokens(urlSelection, tipologia)
    ) {
      return;
    }
    const withMenu = (brands || []).map((b) => {
      const live = menuRef.current?.[b.id];
      if (!Array.isArray(live) || !live.length) return b;
      return { ...b, sources: live };
    });
    onUrlSelectionChange?.(
      buildDefaultUrlSelection(withMenu, brandIds, tipologia),
    );
    // Retick only when the family changes, not on every checkbox edit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tipologia]);

  useEffect(() => {
    const key = tipologiaMenuKey(tipologia);
    if (key === "outerwear") return undefined;
    const targets = (brands || []).filter(
      (b) => (brandIds || []).includes(b.id) && brandHasWebMenu(b),
    );
    if (!targets.length) return undefined;
    const gen = genRef.current + 1;
    genRef.current = gen;
    const timer = setTimeout(async () => {
      const words = [...new Set(menuTokensForTipologia(tipologia).flat())];
      try {
        const learned = await translateMenuWords(words);
        if (genRef.current !== gen) return;
        if (learned) {
          const withMenu = (brands || []).map((b) => {
            const live = menuRef.current?.[b.id];
            if (!Array.isArray(live) || !live.length) return b;
            return { ...b, sources: live };
          });
          const next = buildDefaultUrlSelection(withMenu, brandIds, tipologia);
          selRef.current = next;
          onUrlSelectionChange?.(next);
        }
      } catch {
        /* si la traducción falla, se busca la palabra escrita */
      }
      if (genRef.current !== gen) return;
      setMenusLeft(targets.length);
      let left = targets.length;
      let cursor = 0;
      const worker = async () => {
        while (cursor < targets.length) {
          const brand = targets[cursor];
          cursor += 1;
          if (genRef.current !== gen) return;
          try {
            const data = await getSkillAgentBrandCollections(
              brand.id,
              tipologia,
            );
            if (genRef.current !== gen) return;
            const cols = Array.isArray(data?.collections)
              ? data.collections
              : [];
            if (cols.length) {
              setMenuByBrand((p) => ({ ...p, [brand.id]: cols }));
              const next = applyLiveMenuSelection(
                selRef.current,
                brand,
                cols,
                tipologia,
              );
              selRef.current = next;
              onUrlSelectionChange?.(next);
            }
          } catch {
            /* la marca se queda con las colecciones estimadas */
          } finally {
            left -= 1;
            if (genRef.current === gen) setMenusLeft(Math.max(0, left));
          }
        }
      };
      await Promise.all(
        Array.from({ length: Math.min(4, targets.length) }, () => worker()),
      );
      if (genRef.current === gen) setMenusLeft(0);
    }, 600);
    return () => {
      clearTimeout(timer);
      genRef.current += 1;
      setMenusLeft(0);
    };
    // Solo al cambiar la tipología, no al marcar una casilla.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tipologia]);

  const setBrandOn = (brand, on) => {
    const nextIds = on
      ? [...new Set([...brandIds, brand.id])]
      : brandIds.filter((id) => id !== brand.id);
    onBrandIdsChange?.(nextIds);
    const nextSel = { ...urlSelection };
    if (on) {
      const sources = sourcesFor(brand);
      const preferred = sources.filter((s) => s.defaultSelected);
      const picked = preferred.length
        ? preferred
        : isPantsTipologia(tipologia) ||
            menuTokensForTipologia(tipologia).length
          ? []
          : sources;
      nextSel[brand.id] = picked.map((s) => s.url);
    } else {
      delete nextSel[brand.id];
    }
    onUrlSelectionChange?.(nextSel);
  };

  const setUrlOn = (brand, url, on) => {
    const sources = sourcesFor(brand);
    const current = Array.isArray(urlSelection[brand.id])
      ? urlSelection[brand.id]
      : sources.filter((s) => s.defaultSelected).map((s) => s.url);
    const nextUrls = on
      ? [...new Set([...current, url])]
      : current.filter((u) => u !== url);
    const nextSel = { ...urlSelection, [brand.id]: nextUrls };
    onUrlSelectionChange?.(nextSel);

    if (nextUrls.length && !brandIds.includes(brand.id)) {
      onBrandIdsChange?.([...brandIds, brand.id]);
    } else if (!nextUrls.length && brandIds.includes(brand.id)) {
      onBrandIdsChange?.(brandIds.filter((id) => id !== brand.id));
      const cleared = { ...nextSel };
      delete cleared[brand.id];
      onUrlSelectionChange?.(cleared);
    }
  };

  const loadWebMenu = async (brand) => {
    setLoadingMenu((p) => ({ ...p, [brand.id]: true }));
    setMenuError((p) => ({ ...p, [brand.id]: "" }));
    try {
      const data = await getSkillAgentBrandCollections(brand.id, tipologia);
      const cols = Array.isArray(data?.collections) ? data.collections : [];
      if (!cols.length) {
        setMenuError((p) => ({
          ...p,
          [brand.id]: t("skillAgent.brandSources.menuLoadError"),
        }));
        return;
      }
      setMenuByBrand((p) => ({ ...p, [brand.id]: cols }));
      if (brandIds.includes(brand.id)) {
        const sources = sourcesForTipologia(brand, tipologia, cols);
        const preferred = sources.filter((s) => s.defaultSelected);
        const picked = preferred.length
          ? preferred
          : isPantsTipologia(tipologia) ||
              menuTokensForTipologia(tipologia).length
            ? []
            : sources.slice(0, 3);
        onUrlSelectionChange?.({
          ...urlSelection,
          [brand.id]: picked.map((s) => s.url),
        });
      }
    } catch (err) {
      setMenuError((p) => ({
        ...p,
        [brand.id]:
          err?.response?.data?.error ||
          t("skillAgent.brandSources.menuLoadGenericError"),
      }));
    } finally {
      setLoadingMenu((p) => ({ ...p, [brand.id]: false }));
    }
  };

  const selectAll = () => {
    onBrandIdsChange?.(sorted.map((b) => b.id));
    const withMenu = sorted.map((b) => {
      const live = menuByBrand[b.id];
      if (!Array.isArray(live) || !live.length) return b;
      return { ...b, sources: live };
    });
    onUrlSelectionChange?.(
      buildDefaultUrlSelection(
        withMenu,
        withMenu.map((b) => b.id),
        tipologia,
      ),
    );
  };

  const selectNone = () => {
    onBrandIdsChange?.([]);
    onUrlSelectionChange?.({});
  };

  return (
    <Box>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ mb: 0.75 }}
      >
        <Typography variant="subtitle2" fontWeight={800}>
          {t("skillAgent.brandSources.heading", {
            selected: brandIds.length,
            total: sorted.length,
          })}
        </Typography>
        <Stack direction="row" spacing={0.5}>
          <Button
            size="small"
            sx={{ textTransform: "none", fontWeight: 700 }}
            onClick={selectAll}
          >
            {t("skillAgent.brandSources.selectAll")}
          </Button>
          <Button
            size="small"
            color="warning"
            sx={{ textTransform: "none", fontWeight: 700 }}
            onClick={selectNone}
          >
            {t("skillAgent.brandSources.selectNone")}
          </Button>
        </Stack>
      </Stack>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: "block", mb: 0.75 }}
      >
        {t("skillAgent.brandSources.hint")}
        {menusLeft > 0
          ? ` ${t("skillAgent.brandSources.loadingMenus", { count: menusLeft })}`
          : ""}
      </Typography>
      <Box
        sx={{
          maxHeight,
          overflow: "auto",
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 2,
          px: 1,
        }}
      >
        {sorted.map((b) => {
          const brandOn = brandIds.includes(b.id);
          const sources = sourcesFor(b);
          const selectedUrls = Array.isArray(urlSelection[b.id])
            ? urlSelection[b.id]
            : brandOn
              ? sources.filter((s) => s.defaultSelected).map((s) => s.url)
              : [];
          return (
            <Box
              key={b.id}
              sx={{
                py: 0.75,
                borderBottom: "1px solid",
                borderColor: "divider",
                "&:last-child": { borderBottom: "none" },
              }}
            >
              <Stack
                direction="row"
                alignItems="flex-start"
                justifyContent="space-between"
                spacing={1}
              >
                <FormControlLabel
                  sx={{ alignItems: "flex-start", m: 0, flex: 1 }}
                  control={
                    <Checkbox
                      size="small"
                      checked={brandOn}
                      indeterminate={
                        brandOn &&
                        selectedUrls.length > 0 &&
                        selectedUrls.length < sources.length
                      }
                      onChange={(e) => setBrandOn(b, e.target.checked)}
                    />
                  }
                  label={
                    <Typography
                      variant="body2"
                      fontWeight={700}
                      sx={{ pt: 0.25 }}
                    >
                      {b.name}
                    </Typography>
                  }
                />
                {brandHasWebMenu(b) ? (
                  <Button
                    size="small"
                    sx={{ textTransform: "none", mt: 0.25, flexShrink: 0 }}
                    disabled={!!loadingMenu[b.id]}
                    onClick={() => loadWebMenu(b)}
                    startIcon={
                      loadingMenu[b.id] ? (
                        <CircularProgress size={12} color="inherit" />
                      ) : null
                    }
                  >
                    {t("skillAgent.brandSources.webMenu")}
                  </Button>
                ) : null}{" "}
              </Stack>
              {menuError[b.id] ? (
                <Typography
                  variant="caption"
                  color="error"
                  sx={{ pl: 3.5, display: "block" }}
                >
                  {menuError[b.id]}
                </Typography>
              ) : null}
              {sources.length > 0 && (
                <Stack spacing={0} sx={{ pl: 3.5, pb: 0.25 }}>
                  {sources.map((s) => {
                    const u = s.url;
                    const checked = selectedUrls.includes(u);
                    return (
                      <FormControlLabel
                        key={u}
                        sx={{ m: 0, alignItems: "flex-start" }}
                        control={
                          <Checkbox
                            size="small"
                            checked={checked}
                            disabled={!brandOn && !checked}
                            onChange={(e) => setUrlOn(b, u, e.target.checked)}
                            sx={{ py: 0.25 }}
                          />
                        }
                        label={
                          <Box sx={{ pt: 0.35, minWidth: 0 }}>
                            <Typography
                              variant="caption"
                              fontWeight={800}
                              display="block"
                              sx={{
                                lineHeight: 1.2,
                                letterSpacing: 0.02,
                                textTransform: "uppercase",
                              }}
                            >
                              {s.label || menuLabelFromUrl(u)}
                              {s.productsCount != null
                                ? ` · ${s.productsCount}`
                                : ""}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              display="block"
                              noWrap
                              title={u}
                              sx={{ maxWidth: 420 }}
                            >
                              {u.replace(/^https?:\/\//, "")}
                            </Typography>
                          </Box>
                        }
                      />
                    );
                  })}
                </Stack>
              )}
            </Box>
          );
        })}
        {!sorted.length && (
          <Typography variant="caption" color="text.secondary" sx={{ py: 1 }}>
            {t("skillAgent.brandSources.catalogUnavailable")}
          </Typography>
        )}
      </Box>
    </Box>
  );
}
